import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash';

import {
  type GroupClassIllustrations,
  ChakraFlex,
  ChakraSimpleGrid,
  CheckboxV2,
  FAIconName,
  FireflyButton,
  Icon,
  ModalV2,
  SkeletonLoader,
  Text,
  useToastMessage,
} from '@equip.health/ui';

import {
  GROUP_CLASS_DESCRIPTION_LIMIT,
  GROUP_CLASS_TITLE_LIMIT,
} from '~/lib/constants/calendar.constants';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';

import { DEFAULT_FORM_STATE } from '~/lib/constants/groupClasses.constants';
import { GroupIcons } from '~/components/maud/calendar/group-class/GroupIcons';
import { GroupClassTitleField } from '~/components/maud/calendar/group-class/GroupClassTitleField';
import { GroupClassDescriptionField } from '~/components/maud/calendar/group-class/GroupClassDescriptionField';
import { GroupTypesField } from '~/components/maud/calendar/group-class/GroupTypesField';
import { GroupClassAttendeeTypesField } from '~/components/maud/calendar/group-class/GroupClassAttendeeTypesField';
import { GroupClassTreatmentProgramsField } from '~/components/maud/calendar/group-class/GroupClassTreatmentProgramsField';
import { GroupClassModalitiesField } from '~/components/maud/calendar/group-class/GroupClassModalitiesField';
import { GroupClassDiagnosisField } from '~/components/maud/calendar/group-class/GroupClassDiagnosisField';
import { GroupClassAgeRangeField } from '~/components/maud/calendar/group-class/GroupClassAgeRangeField';
import {
  useCreateGroupV2Mutation,
  useEditGroupV2Mutation,
  useGetGroupClassDetailsV2RawQuery,
} from '~/store/services/groupClass';
import type { GroupDetails } from '../../../../../../lib/util/groups.utils';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';

const TOP_LEVEL_ID = 'create-edit-group-drawer';

const normalizeGroupDetailsToFormState = (
  details?: GroupDetails,
): GroupFormState => ({
  title: typeof details?.title === 'string' ? details.title : '',

  description:
    typeof details?.description === 'string' ? details.description : '',

  iconName:
    typeof details?.iconName === 'string'
      ? (details.iconName as GroupClassIllustrations)
      : undefined,

  attendeeRoles: Array.isArray(details?.attendeeRoles)
    ? details.attendeeRoles.filter(
        (item): item is string => typeof item === 'string',
      )
    : [],

  treatmentPrograms: Array.isArray(details?.treatmentPrograms)
    ? details.treatmentPrograms.filter(
        (item): item is string => typeof item === 'string',
      )
    : [],

  treatmentModalities: Array.isArray(details?.treatmentModalities)
    ? details.treatmentModalities.filter(
        (item): item is string => typeof item === 'string',
      )
    : [],

  diagnoses: Array.isArray(details?.diagnoses)
    ? details.diagnoses.filter(
        (item): item is string => typeof item === 'string',
      )
    : [],

  ageRanges: Array.isArray(details?.ageRanges)
    ? details.ageRanges.filter(
        (item): item is string => typeof item === 'string',
      )
    : [],

  groupTypeCode:
    typeof details?.groupTypeCode === 'string' ? details.groupTypeCode : '',

  isSensitive: Boolean(details?.sensitive),
  isCore: Boolean(details?.core),
});

export { normalizeGroupDetailsToFormState };

interface CreateEditGroupDrawerProps {
  groupExternalId?: string;
  isOpen: boolean;
  onClose: () => void;
  handleSuccess?: () => void;
}

const CreateEditGroupModal = ({
  groupExternalId,
  isOpen,
  onClose,
  handleSuccess,
}: CreateEditGroupDrawerProps) => {
  const isEditMode = !!groupExternalId;
  const { trackEvent } = useAnalytics();

  const { data: groupDetails, isLoading: isGetGroupDetailsLoading } =
    useGetGroupClassDetailsV2RawQuery(groupExternalId, {
      skip: !groupExternalId,
    });

  const [formState, setFormState] =
    useState<GroupFormState>(DEFAULT_FORM_STATE);

  const [initialFormState, setInitialFormState] =
    useState<GroupFormState>(DEFAULT_FORM_STATE);

  const [createGroupV2, { isLoading: isCreatingGroup }] =
    useCreateGroupV2Mutation();
  const [editGroupV2, { isLoading: isEditingGroup }] = useEditGroupV2Mutation();
  const { createToast } = useToastMessage();

  useEffect(() => {
    if (!groupExternalId) {
      setFormState(DEFAULT_FORM_STATE);
      setInitialFormState(DEFAULT_FORM_STATE);
      return;
    }

    if (!groupDetails) {
      return;
    }

    const normalizedData = normalizeGroupDetailsToFormState(groupDetails);

    setFormState(normalizedData);
    setInitialFormState(normalizedData);
  }, [groupExternalId, groupDetails]);

  useEffect(() => {
    if (isOpen && !isGetGroupDetailsLoading) {
      const event = isEditMode
        ? GroupSectionEvents.editGroupModalViewed
        : GroupSectionEvents.createGroupModalViewed;
      trackEvent(event, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
        ...(groupExternalId && { groupId: groupExternalId }),
      });
    }
  }, [isOpen, isGetGroupDetailsLoading, isEditMode, groupExternalId, trackEvent]);

  const updateFormState = useCallback(
    <K extends keyof GroupFormState>(key: K, value: GroupFormState[K]) => {
      setFormState((prev) => ({
        ...prev,
        [key]: Array.isArray(value) ? [...value] : value,
      }));
    },
    [],
  );

  const trackCancelEvent = () => {
    trackEvent(
      isEditMode
        ? GroupSectionEvents.editGroupCancelButtonClicked
        : GroupSectionEvents.createGroupCancelButtonClicked,
      {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      },
    );
  };

  const handleClose = () => {
    if (hasChanges) {
      setIsWarningCloseModalOpen(true);
    } else {
      trackCancelEvent();
      onClose();
    }
  };
  const hasError = useMemo(
    () =>
      !formState.title?.trim() ||
      !formState.iconName ||
      !formState.description?.trim() ||
      !formState.groupTypeCode ||
      formState.title.length > GROUP_CLASS_TITLE_LIMIT ||
      formState.description.length > GROUP_CLASS_DESCRIPTION_LIMIT,
    [formState],
  );

  const hasChanges = useMemo(
    () => !isEqual(formState, initialFormState),
    [formState, initialFormState],
  );

  const [isWarningCloseModalOpen, setIsWarningCloseModalOpen] = useState(false);

  const handleConfirmClose = () => {
    trackCancelEvent();
    setIsWarningCloseModalOpen(false);
    setFormState(DEFAULT_FORM_STATE);
    onClose();
  };

  const handleSubmitClick = () => {
    trackEvent(
      isEditMode
        ? GroupSectionEvents.editGroupSaveChangesButtonClicked
        : GroupSectionEvents.createGroupSaveChangesButtonClicked,
      {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
        'Group title': formState.title,
        'Group description': formState.description,
        'Icon name': formState.iconName,
        'Group type': formState.groupTypeCode,
        'Attendee roles': formState.attendeeRoles,
        'Treatment programs': formState.treatmentPrograms,
        'Treatment modalities': formState.treatmentModalities,
        Diagnoses: formState.diagnoses,
        'Age ranges': formState.ageRanges,
        'Is sensitive': formState.isSensitive,
        'Is core': formState.isCore,
      },
    );

    const request = isEditMode
      ? editGroupV2({ groupId: groupExternalId, groupClassRequest: formState })
      : createGroupV2(formState);

    const successToastMessage = `Group ${isEditMode ? 'updated' : 'created'} successfully.`;
    const errorToastMessage = `Failed to ${isEditMode ? 'update' : 'create'} group.`;

    const toast = (status: 'success' | 'error') => {
      createToast({
        icon:
          status === 'success'
            ? FAIconName.checkRegular
            : FAIconName.exclamationTriangleRegular,
        id: `add-note-template-${status}_toast`,
        text: status === 'success' ? successToastMessage : errorToastMessage,
        variant: status === 'success' ? 'success' : 'warning',
      });
    };

    request
      .unwrap()
      .then(() => {
        handleConfirmClose();
        toast('success');
        handleSuccess?.();
      })
      .catch(() => {
        toast('error');
      });
  };

  const renderFooter = () => (
    <ChakraFlex justifyContent="space-between" gap="12px" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel`}
        variant="secondary"
        onClick={handleClose}
        leftIcon={FAIconName.XmarkRegular}
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__create`}
        onClick={handleSubmitClick}
        leftIcon={FAIconName.checkRegular}
        isDisabled={hasError || (isEditMode && !hasChanges)}
        isLoading={isEditingGroup || isCreatingGroup}
      >
        {isEditMode ? 'Save changes' : 'Create class'}
      </FireflyButton>
    </ChakraFlex>
  );

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit group details' : 'Create group details'}
      size="5xl"
      id={TOP_LEVEL_ID}
    >
      <>
        <SkeletonLoader
          id={`${TOP_LEVEL_ID}__skeleton-loader`}
          isLoaded={!isGetGroupDetailsLoading}
        >
          <ChakraFlex marginTop="4px">
            <ChakraFlex direction="column" gap="24px">
              <ChakraFlex direction="column" gap="18px">
                <GroupIcons
                  value={formState.iconName}
                  onChange={(icon) =>
                    updateFormState('iconName', icon as GroupClassIllustrations)
                  }
                  isNew
                />
                <GroupClassTitleField
                  value={formState.title}
                  onChange={(value) => updateFormState('title', value)}
                />
                <GroupClassDescriptionField
                  value={formState.description}
                  onChange={(value) => updateFormState('description', value)}
                />

                <ChakraFlex
                  direction="column"
                  justifyContent="flex-end"
                  gap="6px"
                >
                  <Text variant="h5">Registration criteria</Text>
                  <Text variant="body">
                    Please select any limitations on registration below. If
                    nothing is selected, the group will be available to all
                    users.
                  </Text>
                </ChakraFlex>

                <ChakraSimpleGrid
                  columns={{ base: 1, md: 2 }}
                  gap="24px"
                  rowGap="18px"
                >
                  <GroupTypesField
                    onChange={(value) =>
                      updateFormState('groupTypeCode', value)
                    }
                    value={formState.groupTypeCode}
                  />
                  <GroupClassAttendeeTypesField
                    onChange={(value) =>
                      updateFormState('attendeeRoles', value)
                    }
                    value={formState.attendeeRoles}
                    leftIcon={FAIconName.userGroupSolid}
                    placeholder="Please choose"
                  />
                  <GroupClassTreatmentProgramsField
                    onChange={(value) =>
                      updateFormState('treatmentPrograms', value)
                    }
                    value={formState.treatmentPrograms}
                    leftIcon={FAIconName.folderMedical}
                    placeholder="Please choose"
                  />
                  <GroupClassModalitiesField
                    onChange={(value) =>
                      updateFormState('treatmentModalities', value)
                    }
                    value={formState.treatmentModalities}
                    leftIcon={FAIconName.bookMedicalRegular}
                    placeholder="Please choose"
                  />
                  <GroupClassDiagnosisField
                    onChange={(value) => updateFormState('diagnoses', value)}
                    value={formState.diagnoses}
                    leftIcon={FAIconName.stethoscopeRegular}
                    placeholder="Please choose"
                  />
                  <GroupClassAgeRangeField
                    onChange={(value) => updateFormState('ageRanges', value)}
                    value={formState.ageRanges}
                    leftIcon={FAIconName.calendarRangeRegular}
                    placeholder="Please choose"
                  />
                </ChakraSimpleGrid>

                <CheckboxV2
                  id={`${TOP_LEVEL_ID}__mark-as-core`}
                  isChecked={formState.isCore}
                  label="Mark as core"
                  onChange={() => updateFormState('isCore', !formState.isCore)}
                />

                <ChakraFlex gap="8px">
                  <CheckboxV2
                    id={`${TOP_LEVEL_ID}__mark-as-sensitive`}
                    isChecked={formState.isSensitive}
                    label="Mark as sensitive"
                    onChange={() =>
                      updateFormState('isSensitive', !formState.isSensitive)
                    }
                  />
                  <Icon color="accent.blue" name={FAIconName.eyeSlashRegular} />
                </ChakraFlex>
              </ChakraFlex>
              {renderFooter()}
            </ChakraFlex>
          </ChakraFlex>
        </SkeletonLoader>
        <DiscardChangesModalV2
          isOpen={isWarningCloseModalOpen}
          onClose={() => setIsWarningCloseModalOpen(false)}
          onConfirm={handleConfirmClose}
        />
      </>
    </ModalV2>
  );
};

export default CreateEditGroupModal;
